import type { AvatarConfig } from '../avatar/types';
import type { LeagueDivision, LeagueId, RankedDelta, RankedStanding } from '../rating/leagues';
import type { DuelXpResult } from '../progress/levels';
import type {
  DailyMissionsSnapshot,
  DuelMissionMetrics,
} from '../missions';
import type { LastCallSnapshot } from '../events/lastCall';
import type { TournamentMailMeta } from '../economy/tournaments';
import type { EventQueueMailMeta } from '../economy/eventQueue';
import { PlayerAction } from './actions';
import { GameEvent } from './events';
import { MatchState, Seat } from './state';

/** One d6 face, 1 through 6. */
export type D6Value = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Seat 0 and seat 1 each roll a d6. The last pair is the deciding roll.
 */
export interface DiceOffResult {
  rounds: [D6Value, D6Value][];
  firstPlayer: Seat;
}

/** Card IDs plus optional parallel foil arrays (same length as each list). */
export interface DeckList {
  main: string[];
  extra: string[];
  mainFoils?: string[];
  extraFoils?: string[];
}

// Socket.IO wire protocol shared by client and server.

export const SOCKET_EVENTS = {
  // Rooms / lobby
  ROOM_CREATE: 'room:create',
  ROOM_CREATED: 'room:created',
  ROOM_JOIN: 'room:join',
  ROOM_JOINED: 'room:joined',
  ROOM_LEAVE: 'room:leave',
  ROOM_UPDATE: 'room:update',
  ROOM_RECONNECT: 'room:reconnect',
  ROOM_ERROR: 'room:error',

  // Open private rooms (waiting, unranked, not vs AI)
  ROOM_BROWSER_JOIN: 'room:browser:join',
  ROOM_BROWSER_LEAVE: 'room:browser:leave',
  ROOM_BROWSER_LIST: 'room:browser:list',

  // Matchmaking
  MATCHMAKING_JOIN: 'matchmaking:join',
  MATCHMAKING_LEAVE: 'matchmaking:leave',
  MATCHMAKING_QUEUED: 'matchmaking:queued',
  MATCHMAKING_FOUND: 'matchmaking:found',
  /** How many accounts are waiting for a Quick Match. */
  MATCHMAKING_QUEUE: 'matchmaking:queue',
  /** Quick Match waiters offered to someone in a vs-AI duel. */
  MATCHMAKING_CALL: 'matchmaking:call',
  /** Vs-AI player asks to take one waiting Quick Match before leaving the CPU duel. */
  MATCHMAKING_CLAIM: 'matchmaking:claim',
  MATCHMAKING_CLAIM_RESULT: 'matchmaking:claim:result',
  /** Drop a claim that this client will not finish. */
  MATCHMAKING_CLAIM_CANCEL: 'matchmaking:claim:cancel',
  /** Server → client. Home strip: next tournament, sign-up, or the live bracket. */
  TOURNAMENT_STATE: 'tournament:state',
  /** Client → server. This account opened the duel page for its match. */
  TOURNAMENT_READY: 'tournament:ready',
  /** Client → server. This account left the waiting page on purpose. */
  TOURNAMENT_UNREADY: 'tournament:unready',
  /** Server → the two players of a waiting match. */
  TOURNAMENT_CALL: 'tournament:call',

  // Game
  GAME_START: 'game:start',
  GAME_ACTION: 'game:action',
  GAME_EVENTS: 'game:events',
  GAME_STATE: 'game:state',
  GAME_STATE_REQUEST: 'game:state:request',
  GAME_ERROR: 'game:error',
  GAME_OVER: 'game:over',
  /** Human PvP chess clock snapshot. */
  GAME_CLOCK: 'game:clock',
  /** Admin watch of a live human PvP room. Read-only. */
  SPECTATE_JOIN: 'spectate:join',
  SPECTATE_LEAVE: 'spectate:leave',
  /** Server → room and spectators: public watchers, unique and sorted. */
  SPECTATE_LIST: 'spectate:list',

  // In-duel PvP chat (the two human seats in a room only)
  CHAT_SEND: 'chat:send',
  CHAT_MESSAGE: 'chat:message',
  CHAT_HISTORY: 'chat:history',

  // Playmat and sleeve chosen for a seat; mid-duel changes included
  COSMETICS_UPDATE: 'cosmetics:update',
  /** A seat opened or closed a graveyard pile. Does not include card ids. */
  GY_GLANCE: 'duel:gy-glance',

  // Site-wide chat (every connected client except the duel socket)
  GLOBAL_JOIN: 'global:join',
  GLOBAL_NICK: 'global:nick',
  GLOBAL_SEND: 'global:send',
  GLOBAL_MESSAGE: 'global:message',
  GLOBAL_HISTORY: 'global:history',
  /**
   * Client → server. The chat tab is visible and was used recently.
   * Engine pings keep answering from a frozen tab; this pulse does not.
   */
  PRESENCE_PULSE: 'presence:pulse',
  /**
   * Server → client. This socket was closed for idleness.
   * Do not reconnect until the player is looking at the page again.
   */
  PRESENCE_SLEEP: 'presence:sleep',
  /** How many accounts are present: a recent pulse, or a seat in a live duel. */
  GLOBAL_ONLINE: 'global:online',
  /** Server → client. Maintenance level changed. */
  SITE_STATUS: 'site:status',
  /** Server → client. Staff credited coins or Master Packs to this account. */
  ACCOUNT_REWARD: 'account:reward',
  /** Server → client. A mailbox note (and optional unclaimed reward) arrived. */
  ACCOUNT_MAIL: 'account:mail',
  /**
   * Server → client. This account's public name changed.
   * Chat lines, mentions, and the live board should show `username`.
   */
  ACCOUNT_RENAME: 'account:rename',
  /** Server → client. Friend list changed. The client refetches. */
  FRIEND_UPDATED: 'friend:updated',
  /** Client → server, and server → both friends. A direct message. */
  FRIEND_DM: 'friend:dm',
  /** Server → sender. The direct message was rejected. */
  FRIEND_DM_ERROR: 'friend:dm:error',
  /** Server → guest. A friend opened a private room for them. */
  FRIEND_INVITE: 'friend:invite',
  /** Guest → server. They declined that invite. */
  FRIEND_INVITE_DECLINE: 'friend:invite:decline',
  /** Server → host. The guest declined. */
  FRIEND_INVITE_DECLINED: 'friend:invite:declined',
} as const;

/** One staff gift, plus the balances after it was applied. */
export interface AccountRewardPayload {
  coins: number;
  packs: number;
  balance: number;
  packCounts: Record<string, number>;
}

/** Placement on one CPU ladder, stored so the client can translate the note. */
export interface SeasonCpuMailMeta {
  seasonId: string;
  difficulty: 'hard' | 'expert' | 'master';
  position: number;
  wins: number;
}

/** Place by finished-duel time, for an expansion special pack. */
export interface PlaytimePackMailMeta {
  position: number;
  /** Set on playtime_pack. Absent on the older playtime_mrd rows. */
  packId?: string;
}

/** One row in the player's mailbox. Rewards stay unclaimed until they claim. */
export interface PlayerMailPayload {
  id: string;
  body: string;
  coins: number;
  packs: number;
  createdAt: string;
  claimedAt: string | null;
  readAt: string | null;
  /** Null on notes written before season mail, and on staff replies. */
  kind:
    | 'season_cpu'
    | 'playtime_mrd'
    | 'playtime_pack'
    | 'tournament'
    | 'event_queue'
    | null;
  meta:
    | SeasonCpuMailMeta
    | PlaytimePackMailMeta
    | TournamentMailMeta
    | EventQueueMailMeta
    | null;
}

/** How the player opened the waiting page. */
export type TournamentEntryVia = 'dialog' | 'bracket' | 'notice' | 'advance'

/** Duel socket tells the server it is here for this bracket match. */
export interface TournamentReadyPayload {
  matchId: string;
  clientId?: string;
  via?: TournamentEntryVia;
}

/** Duel socket tells the server this account left the waiting page. */
export interface TournamentUnreadyPayload {
  matchId: string;
}

export interface PlayerMailListPayload {
  unread: number;
  mail: PlayerMailPayload[];
}

/** Wallet and pack counts after a mailbox reward is claimed. */
export interface PlayerMailClaimPayload {
  id: string;
  balance: number;
  packCounts: Record<string, number>;
  claimedAt: string;
}

export interface RoomPlayer {
  id: string;
  username: string;
  seat: Seat;
  connected: boolean;
  ready: boolean;
  isAI: boolean;
  avatar: AvatarConfig;
  /** Concrete catalog playmat id for this seat. */
  matId: string;
  /** Concrete catalog sleeve id. Omitted on the CPU seat. */
  sleeveId?: string;
}

export type RoomStatus = 'waiting' | 'in_game' | 'finished';

export interface RoomSummary {
  code: string;
  status: RoomStatus;
  players: RoomPlayer[];
  hostId: string;
}

/** One waiting private room on the /rooms browser. */
export interface PrivateRoomRow {
  code: string;
  hostName: string;
  hostAvatar: AvatarConfig;
  seatsTaken: number;
  seatsMax: number;
}

export interface PrivateRoomListPayload {
  rooms: PrivateRoomRow[];
}

// ---- Client -> Server payloads ----

export interface RoomCreatePayload {
  username: string;
  /** stable per-browser identity so a player can reconnect to their seat. */
  clientId?: string;
  /**
   * False when this page is in the background (phone app switch).
   * A hidden page must not pull a live duel off the screen in front.
   * Absent on older clients, which are treated as in front.
   */
  foreground?: boolean;
  /** Create a vs-CPU room and start immediately. */
  vsAI?: boolean;
  /** Saved main/extra IDs. Invalid or missing → server builds a Standard deck. */
  deck?: DeckList;
  avatar?: AvatarConfig;
  /** Concrete catalog playmat id (not `random`). */
  matId?: string;
  /** Concrete catalog sleeve id (not `random`). */
  sleeveId?: string;
  /**
   * Open this room for an accepted friend and hide it from the public list.
   * Absent on a normal private room.
   */
  invite?: string;
}

export interface RoomJoinPayload {
  code: string;
  username: string;
  clientId?: string;
  /** See `RoomCreatePayload.foreground`. */
  foreground?: boolean;
  deck?: DeckList;
  avatar?: AvatarConfig;
  /** Concrete catalog playmat id (not `random`). */
  matId?: string;
  /** Concrete catalog sleeve id (not `random`). */
  sleeveId?: string;
}

export interface MatchmakingJoinPayload {
  username: string;
  clientId?: string;
  /** See `RoomCreatePayload.foreground`. */
  foreground?: boolean;
  deck?: DeckList;
  avatar?: AvatarConfig;
  /** Concrete catalog playmat id (not `random`). */
  matId?: string;
  /** Concrete catalog sleeve id (not `random`). */
  sleeveId?: string;
  /**
   * Leave a vs-AI duel straight into a ranked match with this waiting
   * Quick Match entry. Absent: join the queue as usual.
   * Ignored when `casual` is set.
   */
  callId?: string;
  /** Join the casual queue. League points do not change. */
  casual?: boolean;
  /** Join the live weekly event queue. Ignored when `casual` is set. */
  event?: boolean;
}

/** One Quick Match waiter offered to someone in a vs-AI duel. */
export interface RankedQueueCall {
  id: string;
  username: string;
  /** Null while this account is still in placement. */
  league: LeagueId | null;
  /** Null for King and during placement. */
  division: LeagueDivision | null;
  placementRemaining: number;
  /** Account level from XP. Separate from the league. */
  level: number;
}

/** Server → vs-AI duel: who is waiting in Quick Match right now. */
export interface MatchmakingCallPayload {
  calls: RankedQueueCall[];
}

export interface MatchmakingClaimPayload {
  callId: string;
}

export interface MatchmakingClaimResultPayload {
  callId: string;
  ok: boolean;
}

export interface RoomReconnectPayload {
  code: string;
  clientId: string;
  /** See `RoomCreatePayload.foreground`. */
  foreground?: boolean;
}

/** Client → server: drop this socket's seat. `forfeit` ends a live PvP duel now. */
export interface RoomLeavePayload {
  forfeit?: boolean;
}

export interface GameActionPayload {
  code: string;
  action: PlayerAction;
}

/** Open a read-only view of a live human PvP room. */
export interface SpectateJoinPayload {
  code: string;
  /**
   * Admin omniscient watch. Same secret the panel sends as `x-admin-key`.
   * Omitted for a public spectator (hands stay hidden).
   */
  key?: string;
}

/** Server → room and spectators. Admin watches are omitted. */
export interface SpectateListPayload {
  names: string[];
}

/** Client → server: this seat's playmat and sleeve for the current duel. */
export interface CosmeticsUpdatePayload {
  code: string;
  matId: string;
  /** Concrete catalog sleeve id. Omitted by older clients. */
  sleeveId?: string;
}

/** Server → room and spectators: a seat's cosmetics changed. */
export interface CosmeticsSyncPayload {
  seat: Seat;
  matId: string;
  sleeveId?: string;
}

/** Client → server: this seat opened or closed a graveyard viewer. */
export interface GyGlanceSendPayload {
  code: string;
  /** Whose pile. Ignored when `open` is false. */
  whose: Seat;
  open: boolean;
}

/** Server → room: someone opened or closed a graveyard viewer. */
export interface GyGlancePayload {
  /** Seat that is looking. */
  viewer: Seat;
  /** Whose pile they opened. */
  whose: Seat;
  open: boolean;
}

export interface ChatSendPayload {
  code: string;
  text: string;
}

export interface ChatMessagePayload {
  id: string;
  seat: Seat;
  username: string;
  text: string;
  at: number;
  avatar: AvatarConfig;
  /** Connected names this line tagged when it was sent. */
  mentions?: string[];
}

export interface ChatHistoryPayload {
  messages: ChatMessagePayload[];
}

export interface GlobalJoinPayload {
  username: string;
  clientId?: string;
  avatar?: AvatarConfig;
}

export interface GlobalNickPayload {
  username: string;
  avatar?: AvatarConfig;
}

export interface GlobalSendPayload {
  text: string;
}

export interface GlobalChatMessagePayload {
  id: string;
  username: string;
  text: string;
  at: number;
  clientId: string;
  avatar: AvatarConfig;
  /** Connected names this line tagged when it was sent. */
  mentions?: string[];
}

export interface GlobalChatHistoryPayload {
  messages: GlobalChatMessagePayload[];
}

/** Public name change. `previous` is the name chat was showing. */
export interface AccountRenamePayload {
  previous: string;
  username: string;
}

/** One direct-message line. `from` wrote it to `to`. */
export interface DirectMessagePayload {
  id: string;
  from: string;
  to: string;
  text: string;
  at: number;
}

export interface FriendDmErrorPayload {
  code: string;
  username?: string;
}

/** Host is waiting in `code`. The room is not on the public list. */
export interface FriendInvitePayload {
  username: string;
  code: string;
}

/** A tournament duel is waiting on this account. */
export interface TournamentCallPayload {
  matchId: string;
  opponent: string;
}

/** The guest who declined. */
export interface FriendInviteDeclinedPayload {
  username: string;
}

/** Accounts with a recent presence pulse, or a seat in a live duel. */
export interface GlobalOnlinePayload {
  count: number;
  /** Display names of those accounts. Absent on older servers. */
  users?: string[];
}

// ---- Server -> Client payloads ----

export interface RoomCreatedPayload {
  room: RoomSummary;
  seat: Seat;
}

export interface RoomJoinedPayload {
  room: RoomSummary;
  seat: Seat;
}

export interface MatchmakingFoundPayload {
  code: string;
  seat: Seat;
}

/** Accounts waiting in the Quick Match queue right now. */
export interface MatchmakingQueuePayload {
  count: number;
  /** Accounts waiting in the casual queue. Absent on older servers. */
  casual?: number;
  /** Accounts waiting in the weekly event queue. Absent on older servers. */
  event?: number;
  /** True while both queues refuse new duels. */
  closed?: boolean;
}

/** 1-based ladder place. Null when this duelist is not on that board. */
export interface DuelRosterPlaces {
  ranked: number | null;
  level: number | null;
  ai: {
    hard: number | null;
    expert: number | null;
    master: number | null;
  };
}

/** Finished human PvP results. Win rate is wins / (wins + losses). */
export interface DuelRosterRecord {
  wins: number;
  losses: number;
  draws: number;
  /**
   * Current run of the same human PvP result, newest duel first.
   * Null when that run is shorter than 2, or when the latest duel was a draw.
   */
  streak: { kind: 'win' | 'loss'; count: number } | null;
}

/**
 * Public snapshot shown before a human PvP duel.
 * Seat order matches MatchState.seats: index 0, then index 1.
 */
export interface DuelRosterCard {
  level: number;
  standing: RankedStanding | null;
  places: DuelRosterPlaces;
  record: DuelRosterRecord;
}

/**
 * A perspective-filtered view of the match for one seat (opponent hand/deck hidden).
 * `viewerSeat` tells the renderer which side is "you".
 */
export interface GameStatePayload {
  state: MatchState;
  viewerSeat: Seat;
  /** Present on GAME_START so both clients play the same d6 first-player roll. */
  dice?: DiceOffResult;
  /**
   * Human PvP only. Both clients share this snapshot for the roster intro.
   * Absent vs CPU, guests, or when the database could not be read.
   */
  roster?: [DuelRosterCard, DuelRosterCard];
}

export interface GameEventsPayload {
  events: GameEvent[];
  /** authoritative state snapshot AFTER the events were applied (perspective-filtered). */
  state: MatchState;
  /** which seat this perspective-filtered snapshot belongs to ("you"). */
  viewerSeat: Seat;
  /** Replay playback: dwell on every action, including the recorded player's. */
  replay?: boolean;
}

/** Coins credited to one human seat when a match ends. */
export interface CoinReward {
  seat: Seat;
  delta: number;
  coins: number;
  /**
   * Coins from the ranked-play offer, already included in `delta`.
   * Present only when this match paid that offer.
   */
  nudge?: number;
  /** Offers still waiting after this match. Sent on a finished Quick Match. */
  nudgePending?: number;
  /** Call-chip claims used today after this match. */
  callClaims?: number;
}

/**
 * Collection milestones already paid to this account.
 * Sent with GET /account/state. Progress itself is derived from the album.
 */
export interface AccountCollectionClaimed {
  collectionClaimed: string[];
}

/** GAME_OVER socket payload (and the GameClient onOver argument). */
export interface GameOverPayload {
  winner: Seat | null;
  winReason?: string | null;
  rewards?: CoinReward[];
  /** Viewer's ranked standing after this Quick Match. Absent if unranked. */
  ranked?: RankedDelta;
  /** Viewer's account XP after this duel. Absent if progress could not apply. */
  progress?: DuelXpResult;
  /** Viewer's daily missions after this duel. Absent if they could not apply. */
  missions?: DailyMissionsSnapshot;
  /** Viewer's Última llamada progress after a finished Quick Match. */
  lastCall?: LastCallSnapshot;
  /** Local vs-AI: engine counters for the viewer's seat. */
  metrics?: DuelMissionMetrics;
  /**
   * This vs-AI duel unlocked a free coin offer for playing one Quick Match.
   */
  rankedOffer?: {
    coins: number;
    pending: number;
  };
}
